import { useContext, useState, useRef } from 'react';
import axios from 'axios';
import { FormControl, Grid, Typography, IconButton, Box as MuiBox } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import { BaseTypography } from '../common/PublicTypography';
import { InputLabel, InputText, BaseButton } from '../common/Form';
import { getApiBaseUrl } from '../../utils/env';
import { CalculatorContext } from '../../context/calculator/CalculatorProvider';
import { numberWithoutComma } from '../calculator/utils';
import Box from '@mui/material/Box';

const FormLegacy = styled('form')`
  background: #ebf1ff;
  margin-top: ${({ theme }) => theme.spacing(3.5)};
  padding: ${({ theme }) => theme.spacing(2.5)};
  color: #2e434e;
  text-align: left;
  border-radius: 4px;

  ${props => props.theme.breakpoints.up('md')} {
    max-width: 840px;
    margin-left: auto;
    margin-right: auto;
    padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(10)};
  }
`;

const Form = styled('form')`
  padding: ${({ theme }) => theme.spacing(2)};
  border-radius: 0 0 9px 9px;
  color: #2e434e;
  background: #ebf1ff;
  text-align: left;
`;

const ContactFormHead = styled(Box)`
  background: #214bde;
  border-radius: 4px;
  padding: 45px 16px;
`;

export const Headline = styled(BaseTypography)`
  font-size: 38px;
  line-height: 1.14;
  max-width: 280px;
  ${props => props.theme.breakpoints.up('md')} {
    font-size: 64px;
    text-align: center;
    max-width: none;
  }
`;

export const Paragraph = styled(Typography)`
  font-size: 20px;
  line-height: 1.3;
  max-width: 280px;
  margin-top: 20px;
  ${props => props.theme.breakpoints.up('md')} {
    text-align: center;
    max-width: none;
  }
`;

const FormGridRoot = styled(Grid)`
  ${props => props.theme.breakpoints.up('md')} {
    max-width: 840px;
    margin: 0 auto;
  }
`;

const ChannelFieldWrapper = styled(MuiBox)`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  
  .field-container {
    flex: 1;
  }
  
  .button-container {
    display: flex;
    gap: 4px;
    align-items: center;
    margin-top: 28px;
  }
`;

const HoneypotField = styled('input')`
  position: absolute;
  left: -9999px;
  opacity: 0;
  height: 0;
  width: 0;
  pointer-events: none;
`;

const ContactForm = ({ className, contactFormHead, legacy }) => {
  const { calc } = useContext(CalculatorContext);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [oneLineBrief, setOneLineBrief] = useState('');
  const [creatorChannels, setCreatorChannels] = useState(['']);
  const [isFormSent, setIsFormSent] = useState(false);
  const [website, setWebsite] = useState('');
  const formLoadTime = useRef(Date.now());
  const MIN_SUBMIT_TIME_MS = 3000; // 3 seconds minimum

  const addChannelField = () => {
    if (creatorChannels.length < 6) {
      setCreatorChannels([...creatorChannels, '']);
    }
  };

  const removeChannelField = (index) => {
    if (creatorChannels.length > 1) {
      const newChannels = creatorChannels.filter((_, i) => i !== index);
      setCreatorChannels(newChannels);
    }
  };

  const updateChannelField = (index, value) => {
    const newChannels = [...creatorChannels];
    newChannels[index] = value;
    setCreatorChannels(newChannels);
  };

  const submitForm = payload => {
    axios.post(`${getApiBaseUrl()}/public/enquiry`, payload).then(response => {
      if (response.status === 201) {
        setIsFormSent(true);
        setName('');
        setEmail('');
        setOneLineBrief('');
        setCreatorChannels(['']);
      }
    });
  };

  const FormRoot = legacy ? FormLegacy : Form;
  const FormRowRoot = legacy ? Grid : FormGridRoot;

  return (
    <Grid container className={className}>
      <Grid item xs={12} justifyContent={'center'}>
        {!legacy && (
          <ContactFormHead>
            {!contactFormHead && (
              <>
                <Headline variant={'h1'}>Contact us now</Headline>
                <Paragraph variant={'body1'}>Enter your details below and we'll be back in touch soon</Paragraph>
              </>
            )}
            {contactFormHead}
          </ContactFormHead>
        )}
        {legacy && (
          <>
            <Headline variant={'h1'}>Contact us</Headline>
            <Paragraph variant={'body1'}>Take your creator content beyond the feed into high-attention environments — contact us now to get started</Paragraph>
          </>
        )}
        <FormRoot
          onSubmit={e => {
            e.preventDefault();
            const elapsed = Date.now() - formLoadTime.current;
            if (website || elapsed < MIN_SUBMIT_TIME_MS) return; // bot trap
            submitForm({
              name,
              email,
              oneLineBrief,
              existingCreatorChannel: creatorChannels.filter(ch => ch.trim() !== '').join(','),
              dailyPageviews: numberWithoutComma(calc?.dailyPageviews || '') || null,
              matchPercentage: calc?.matchPercentage || null,
              currentAverageCpm: calc?.autoCpm || null,
              isAutoplay: calc?.isCtp !== undefined ? String(!calc.isCtp) : null,
              fillPercentage: calc?.fillRatePercentage || null,
              includeHeaderbidding: calc?.includeHeaderbidding !== undefined ? String(calc.includeHeaderbidding) : null,
              isProgrammatic: calc?.isProgrammatic ? String(calc.isProgrammatic) : null,
              percentagePagesMissingVideo: calc?.imageToVideoRatioPercentage || null,
            });
          }}
        >
          <FormRowRoot container spacing={2}>
            {/* Honeypot field - hidden from humans, bots will fill it */}
            <HoneypotField
              type="text"
              name="website"
              value={website}
              onChange={e => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
            <Grid item md={6} xs={12}>
              <FormControl margin="dense" fullWidth>
                <InputLabel htmlFor="name">Name*</InputLabel>
                <InputText
                  id={'name'}
                  onChange={({ target }) => setName(target.value)}
                  required
                  value={name}
                  placeholder={'Your Name'}
                />
              </FormControl>
            </Grid>
            <Grid item md={6} xs={12}>
              <FormControl margin="dense" fullWidth>
                <InputLabel htmlFor="email">Email*</InputLabel>
                <InputText
                  id={'email'}
                  onChange={({ target }) => setEmail(target.value)}
                  required
                  value={email}
                  placeholder={'Your Email'}
                  type={'email'}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl margin="dense" fullWidth>
                <InputLabel htmlFor="oneLineBrief">One line brief</InputLabel>
                <InputText
                  id={'oneLineBrief'}
                  multiline
                  rows={4}
                  onChange={({ target }) => setOneLineBrief(target.value)}
                  placeholder={'Brand awareness campaign for Champions League next week'}
                  value={oneLineBrief}
                  InputProps={{
                    style: {
                      paddingLeft: 0,
                      paddingRight: 0,
                      paddingTop: 0,
                      paddingBottom: 0,
                      background: 'white'
                    }
                  }}
                />
              </FormControl>
            </Grid>
            {creatorChannels.map((channel, index) => (
              <Grid item xs={12} key={index}>
                <ChannelFieldWrapper>
                  <div className="field-container">
                    <FormControl margin="dense" fullWidth>
                      <InputLabel htmlFor={`existingCreatorChannel-${index}`}>
                        Existing creator channel {index > 0 ? `#${index + 1}` : ''}
                      </InputLabel>
                      <InputText
                        id={`existingCreatorChannel-${index}`}
                        onChange={({ target }) => updateChannelField(index, target.value)}
                        placeholder={index === 0 ? 'www.youtube.com/sidemen' : ''}
                        value={channel}
                      />
                    </FormControl>
                  </div>
                  <div className="button-container">
                    {index === 0 ? (
                      <>
                        <IconButton
                          onClick={addChannelField}
                          disabled={creatorChannels.length >= 6}
                          sx={{ color: '#274bde' }}
                          size="small"
                        >
                          <AddIcon />
                        </IconButton>
                        <IconButton
                          disabled
                          sx={{ color: 'transparent', cursor: 'default', pointerEvents: 'none', visibility: 'hidden' }}
                          size="small"
                        >
                          <RemoveIcon />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton
                          onClick={addChannelField}
                          disabled={creatorChannels.length >= 6}
                          sx={{ color: '#274bde' }}
                          size="small"
                        >
                          <AddIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => removeChannelField(index)}
                          sx={{ color: '#274bde' }}
                          size="small"
                        >
                          <RemoveIcon />
                        </IconButton>
                      </>
                    )}
                  </div>
                </ChannelFieldWrapper>
              </Grid>
            ))}
            <Grid item xs={12}>
              <Grid container justifyContent={'center'}>
                <Grid item>
                  <BaseButton type={'submit'} disabled={isFormSent} variant={'contained-x'} fullWidth>
                    {isFormSent ? "We'll be in touch within 24 hours" : 'Send'}
                  </BaseButton>
                </Grid>
              </Grid>
            </Grid>
          </FormRowRoot>
        </FormRoot>
      </Grid>
    </Grid>
  );
};

ContactForm.propTypes = {
  className: PropTypes.string,
  contactFormHead: PropTypes.element,
  legacy: PropTypes.bool,
};

export default styled(ContactForm)``;
